import { FC, useEffect } from 'react';
import { InventoryCheckFormData, InventoryCheckModalProps } from './InventoryCheckModal.types';
import { StyledInventoryCheckModal } from './InventoryCheckModal.styles';
import { Button, Modal, baseVariables } from '@marriott/mi-ui-library';
import { DatePickerDesktop, DatePickerMobileContainer } from '../../../../../../molecules';
import { Controller, useForm } from 'react-hook-form';
import { useMediaQuery } from '../../../../../../hooks';
import {
  getFormattedCalendarDate,
  getFormattedDateObject,
  updateDataLayerWithInventoryAttributes,
} from '../../../../../../utils';

export const InventoryCheckModal: FC<InventoryCheckModalProps> = ({
  show,
  labels,
  defaultValues,
  analyticsCount,
  isMarshaProperty = true,
  closeModal,
  onConfirmInventoryCheck,
}) => {
  const {
    heading,
    acrsHeading,
    reslinkWithoutInventoryInfo,
    toResolveLabelInfo,
    reviewMarshaInfo,
    reviewACRSInfo,
    checkShoulderNightsInfo,
    dateNotes,
    dateNotesACRS,
    confirm,
    cancel,
    dateLabels,
    confirmTrackDescription,
    confirmTrackLocation,
  } = labels;

  const { doneTrackLocation, doneTrackDescription } = dateLabels;
  const { startDate, endDate } = defaultValues;
  const isDesktop = useMediaQuery(baseVariables.mediaQuery.lg);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    params.set('inventoryCheck', 'missing');
    window.history.replaceState({}, '', `?${params.toString()}`);
    updateDataLayerWithInventoryAttributes(params.toString());

    return () => {
      params.delete('inventoryCheck');
      window.history.replaceState({}, '', `?${params.toString()}`);
      updateDataLayerWithInventoryAttributes(params.toString());
    };
  }, []);

  const { control, getValues, setValue } = useForm<InventoryCheckFormData>({
    defaultValues: {
      dates: '',
      overriddenStartDate: '',
      overriddenEndDate: '',
    },
  });

  const handleDatesChange = (dates: string, fromDate: string, toDate: string) => {
    const formattedFromDate = fromDate && getFormattedCalendarDate(fromDate, 'hyphenatedDateWithMonthNoAndYear');
    const formattedToDate = toDate && getFormattedCalendarDate(toDate, 'hyphenatedDateWithMonthNoAndYear');
    if (formattedFromDate !== startDate || formattedToDate !== endDate) {
      setValue('dates', dates);
      setValue('overriddenStartDate', formattedFromDate);
      setValue('overriddenEndDate', formattedToDate);
    }
  };

  const handleConfirm = () => {
    const formValues = getValues();
    const selectedStartDate = formValues.overriddenStartDate;
    const selectedEndDate = formValues.overriddenEndDate;

    if (selectedStartDate && selectedEndDate) {
      onConfirmInventoryCheck({
        overriddenStartDate: selectedStartDate,
        overriddenEndDate: selectedEndDate,
      });
    } else {
      closeModal();
    }
  };

  const datesField = (
    <Controller
      control={control}
      name="dates"
      render={() => (
        <DatePickerDesktop
          dates={dateLabels}
          align={'left'}
          showFlexibleDates={false}
          selectedStartDate={getFormattedDateObject(startDate)}
          selectedEndDate={getFormattedDateObject(endDate)}
          isDateRangeFixed={true}
          rangeStartDate={getFormattedDateObject(startDate)}
          rangeEndDate={getFormattedDateObject(endDate)}
          onChange={handleDatesChange}
          doneBtnClickTrackLocation={`${doneTrackLocation}|${doneTrackDescription}-click-${analyticsCount}|internal`}
        />
      )}
    />
  );

  const mobileDatesField = (
    <Controller
      control={control}
      name="dates"
      render={() => (
        <DatePickerMobileContainer
          dates={dateLabels}
          startDate={getFormattedDateObject(startDate)}
          endDate={getFormattedDateObject(endDate)}
          isDateRangeFixed={true}
          flexibleDates={false}
          rangeStartDate={getFormattedDateObject(startDate)}
          rangeEndDate={getFormattedDateObject(endDate)}
          onChange={handleDatesChange}
          doneBtnClickTrackLocation={`${doneTrackLocation}|${doneTrackDescription}-click-${analyticsCount}|internal`}
        />
      )}
    />
  );

  return (
    <StyledInventoryCheckModal
      modalId="inventoryCheckModal"
      labelledBy="inventoryCheckModal"
      role="dialog"
      show={show}
      popupOpenState={show}
      disableScrollOnBody={true}
      secondaryClassName="mt-lg-2"
      handleBlur={!!isDesktop}
      onClose={closeModal}
      setPopupOpenState={closeModal}
    >
      <Modal.Header
        labelText={isMarshaProperty ? heading : acrsHeading}
        popupHeaderOnCLoseFunc={closeModal}
      ></Modal.Header>
      <Modal.Body className="px-5 custom-scrollbar modal-body inventory-modal-body">
        <p className="mb-0">
          <span className="d-inline-block mb-2">{`${reslinkWithoutInventoryInfo} `}</span>
          <span className="d-inline-block mb-2">{`${toResolveLabelInfo} `}</span>
        </p>
        <ol>
          <li>{isMarshaProperty ? reviewMarshaInfo : reviewACRSInfo}</li>
          <li>{checkShoulderNightsInfo}</li>
        </ol>
        <div className="row">
          <div className="col-md-12 mb-md-0">{isDesktop ? datesField : mobileDatesField}</div>
          <p className="notes">
            <span>{`${isMarshaProperty ? dateNotes : dateNotesACRS}`}</span>
          </p>
        </div>
      </Modal.Body>
      <Modal.FooterGeneric
        className="modal-footer inventory-modal-footer"
        fadeEffect={true}
        actions={
          <>
            <Button className={'m-button-secondary'} buttonCopy={cancel} callback={closeModal} />
            <Button
              className={'m-button-primary'}
              buttonCopy={confirm}
              callback={handleConfirm}
              custom_click_track_value={`${confirmTrackLocation}|${confirmTrackDescription}-click-${analyticsCount}|internal`}
            />
          </>
        }
      ></Modal.FooterGeneric>
    </StyledInventoryCheckModal>
  );
};
